<template>
  <div class="configurator">
    <b-card
      no-body
      class="configurator-header mb-2"
    >
      <div class="configurator-topbar">
        <div>
          <div class="configurator-label">
            {{ order ? $t('configurator.offer') : $t('configurator.new_offer') }}
          </div>
          <div class="configurator-offer-id">
            {{ offerNumber }}
          </div>
        </div>
        <div
          v-if="client"
          class="configurator-client"
        >
          <strong>{{ client.name }}</strong><br>
          <span class="configurator-client-place">{{ [client.town, client.province].filter(Boolean).join(', ') }}</span>
        </div>
        <div class="ml-auto">
          <span
            class="cfg-badge dot"
            :class="statusBadge.class"
          >{{ statusBadge.text }}</span>
        </div>
      </div>
      <nav class="configurator-stepper">
        <div
          v-for="(label, index) in steps"
          :key="label"
          class="configurator-step"
          :class="{ active: step === index + 1, done: step > index + 1, disabled: !canGoTo(index + 1) }"
          @click="goTo(index + 1)"
        >
          <span class="configurator-step-num">{{ index + 1 }}</span>
          <span class="configurator-step-label">{{ label }}</span>
        </div>
      </nav>
    </b-card>

    <step-selection
      v-if="step === 1"
      @next="goTo(2)"
    />
    <step-configuration
      v-if="step === 2"
      @prev="goTo(1)"
      @next="goTo(3)"
    />
    <step-extras
      v-if="step === 3"
      @prev="goTo(2)"
      @next="goTo(4)"
    />
    <step-summary
      v-if="step === 4"
      @goto="goTo"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { BCard } from 'bootstrap-vue'
import StepSelection from './components/StepSelection.vue'
import StepConfiguration from './components/StepConfiguration.vue'
import StepExtras from './components/StepExtras.vue'
import StepSummary from './components/StepSummary.vue'

export default {
  components: {
    BCard,
    StepSelection,
    StepConfiguration,
    StepExtras,
    StepSummary,
  },
  computed: {
    ...mapGetters({
      step: 'configurator/getStep',
      items: 'configurator/getItems',
      order: 'configurator/getOrder',
      legacy: 'configurator/isLegacy',
      clientId: 'configurator/getClientId',
      selectedClient: 'clients/getClient',
    }),
    client() {
      return this.selectedClient && this.selectedClient.id === this.clientId ? this.selectedClient : null
    },
    steps() {
      return [
        this.$t('configurator.step_selection'),
        this.$t('configurator.step_configuration'),
        this.$t('configurator.step_extras'),
        this.$t('configurator.step_summary'),
      ]
    },
    // Como en el prototipo: "En progreso" mientras la oferta se puede editar
    statusBadge() {
      const type = this.order ? this.order.status_last.type : 'pending'
      if (type === 'pending') return { class: 'cfg-badge-orange', text: this.$t('configurator.in_progress') }
      if (type === 'revision') return { class: 'cfg-badge-primary', text: this.$t('configurator.order_status.revision') }
      return { class: 'cfg-badge-green', text: this.$t(`configurator.order_status.${type}`) }
    },
    offerNumber() {
      if (!this.order) return '—'
      return `${new Date(this.order.created_at * 1000).getFullYear()}/T${this.order.id}`
    },
  },
  methods: {
    ...mapActions({
      loadCatalog: 'configurator/loadCatalog',
      loadOffer: 'configurator/loadOffer',
      getClient: 'clients/getClient',
      restoreDraft: 'configurator/restoreDraft',
      saveDraft: 'configurator/saveDraft',
    }),
    ...mapMutations({
      setStep: 'configurator/setStep',
      resetOffer: 'configurator/resetOffer',
    }),
    canGoTo(step) {
      if (this.legacy) return step === 4
      return step === 1 || this.items.length > 0
    },
    goTo(step) {
      if (!this.canGoTo(step)) return
      this.setStep(step)
      window.scrollTo(0, 0)
    },
  },
  async created() {
    await this.loadCatalog()
    if (this.$route.params.id) {
      const draftStep = await this.loadOffer(this.$route.params.id)
      this.setStep(draftStep || 4)
    } else if (!await this.restoreDraft()) {
      this.resetOffer()
    }
    this.unwatchDraft = this.$store.watch(
      (state, getters) => getters['configurator/getDraft'],
      () => this.saveDraft(),
      { deep: true },
    )
    await this.getClient(this.clientId)
  },
  beforeDestroy() {
    if (this.unwatchDraft) this.unwatchDraft()
  },
}
</script>

<style lang="scss">
@import './configurator.scss';
</style>
